import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Sparkles, Zap, Target } from 'lucide-react';

interface ComingSoonProps {
  title?: string;
  description?: string;
  backTo?: string;
  backLabel?: string;
  layout?: 'user' | 'admin';
}

const ComingSoon: React.FC<ComingSoonProps> = ({ 
  title = "Coming Soon", 
  description = "We're working hard to bring you something amazing!",
  backTo = "/",
  backLabel = "Go Back",
  layout = "user"
}) => {
  const navigate = useNavigate();

  const features = [
    { icon: Zap, text: "Lightning Fast Performance" },
    { icon: Target, text: "User-Focused Features" },
    { icon: Sparkles, text: "Beautiful Interface" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 flex items-center justify-center p-3 sm:p-4">
      <div className="max-w-xl lg:max-w-2xl w-full">
        {/* Back Button */}
        <div className="mb-4 sm:mb-6 lg:mb-8">
          <button
            onClick={() => navigate(backTo)}
            className="flex items-center text-purple-600 hover:text-purple-800 transition-colors duration-200 group"
          >
            <ArrowLeft className="mr-1 sm:mr-2 group-hover:-translate-x-1 transition-transform duration-200" size={18} />
            <span className="font-medium text-sm sm:text-base">{backLabel}</span>
          </button>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-lg sm:rounded-2xl shadow-2xl p-4 sm:p-6 lg:p-8 xl:p-12 text-center border border-purple-100">
          {/* Animated Icon */}
          <div className="mb-4 sm:mb-6 lg:mb-8 flex justify-center">
            <div className="relative">
              <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center shadow-xl">
                <Clock className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-white animate-pulse" />
              </div>
              <div className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 w-6 h-6 sm:w-8 sm:h-8 bg-yellow-400 rounded-full flex items-center justify-center animate-bounce">
                <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
              </div>
            </div>
          </div>

          {/* Title */}
          <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-3 sm:mb-4">
            {title}
          </h1>

          {/* Description */}
          <p className="text-gray-600 text-sm sm:text-base lg:text-lg xl:text-xl mb-4 sm:mb-6 lg:mb-8 leading-relaxed max-w-lg mx-auto">
            {description}
          </p>

          {/* Features Preview */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6 lg:mb-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div 
                  key={index}
                  className="p-3 sm:p-4 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg sm:rounded-xl border border-purple-100 hover:shadow-lg transition-all duration-300"
                >
                  <IconComponent className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600 mx-auto mb-2 sm:mb-3" />
                  <p className="text-xs sm:text-sm font-medium text-gray-700">{feature.text}</p>
                </div>
              );
            })}
          </div>

          {/* Progress Indicator */}
          <div className="mb-4 sm:mb-6 lg:mb-8">
            <div className="flex items-center justify-between mb-1 sm:mb-2">
              <span className="text-xs sm:text-sm font-medium text-gray-600">Development Progress</span>
              <span className="text-xs sm:text-sm font-bold text-purple-600">75%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3 shadow-inner">
              <div 
                className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 sm:h-3 rounded-full shadow-sm transition-all duration-1000 ease-out" 
                style={{ width: '75%' }}
              ></div>
            </div>
          </div>

          {/* Notification */}
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 lg:mb-8">
            <p className="text-xs sm:text-sm text-yellow-800">
              <span className="font-semibold">🎉 Stay tuned!</span> This feature will be available very soon. We're adding the finishing touches!
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <button
              onClick={() => navigate(backTo)}
              className="px-4 sm:px-6 lg:px-8 py-2 sm:py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg sm:rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 text-sm sm:text-base"
            >
              {backLabel}
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-4 sm:px-6 lg:px-8 py-2 sm:py-3 border-2 border-purple-200 text-purple-600 rounded-lg sm:rounded-xl hover:bg-purple-50 transition-all duration-200 font-semibold text-sm sm:text-base"
            >
              Check Again
            </button>
          </div>
        </div>

        {/* Bottom Note */}
        <div className="text-center mt-4 sm:mt-6 lg:mt-8">
          <p className="text-gray-500 text-xs sm:text-sm">
            Thank you for your patience! 🙏
          </p>
        </div>
      </div>
    </div>
  );
};

export default ComingSoon;